import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/utils/query-keys';
import { useGetDownloadVideoURL } from '@/hooks/useGetVideoURL';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useTasks } from '@/components/views/BeraJob/hooks/hooks';
import { toStretchesFromReports, toRangesFromStretches } from '@/components/views/BeraJob/BeraReportV2/utils';
import { ALL_REPORTS_FILTER } from '@/components/views/BeraJob/BeraReportV2/constants';
import {
	useOutOfRangeStretches,
	useRulerEditing,
	useStretchDraft,
	useStretchLoop,
	useStretchPersistence,
	useVideoPatchStatus
} from '@/components/views/BeraJob/BeraReportV2/_hooks';
import { useBeraReportsByJobSummary } from '@/components/views/BeraJob/hooks/useBeraReportV2';

import type { VideoPlayerHandle } from '@/components/ui/@DesignSystem/VideoPlayer/VideoPlayer';
import type { FileForCustomReport } from '@/components/ui/Inputs/types/response/file';
import type { BeraJobSummary, BeraReportV2 } from '@/types/Bera';
import type { Context, Methods, State } from './types';

interface BeraReportV2ProviderProps {
	file: FileForCustomReport;
	jobSummary: BeraJobSummary;
	children: JSX.Element;
}

const BeraReportV2Context = createContext<Context | undefined>(undefined);

export function BeraReportV2Provider({ file, jobSummary, children }: Readonly<BeraReportV2ProviderProps>): JSX.Element {
	const queryClient = useQueryClient();
	const { organization, company } = useApplicationContext();

	const playerRef = useRef<VideoPlayerHandle | null>(null);

	const [videoDuration, setVideoDuration] = useState<number>(0);
	const [selectedReport, setSelectedReport] = useState<BeraReportV2 | undefined>(undefined);
	const [isDraggingStretch, setIsDraggingStretch] = useState<boolean>(false);
	const [hasUnsavedNoRiskRemoval, setHasUnsavedNoRiskRemoval] = useState<boolean>(false);
	const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null);

	const organizationId = organization?.id ?? '';
	const companyId = company?.id ?? '';
	const jobSummaryId = jobSummary?.id;
	const cycleId = jobSummary?.cycle_id;

	const patch = useVideoPatchStatus({ organizationId, companyId, jobSummary });

	const video = useGetDownloadVideoURL({
		organization_id: organizationId,
		company_id: companyId,
		file_id: file.id,
		original_name: file.original_name,
		isVideoUnavailable: patch.isVideoUnavailable
	});

	const tasks = useTasks({
		organization_id: organizationId,
		company_id: companyId,
		cycle_id: cycleId
	});

	const reports = useBeraReportsByJobSummary({
		organization_id: organizationId,
		company_id: companyId,
		bera_job_summary_id: jobSummaryId,
		...ALL_REPORTS_FILTER
	});

	const allReports = useMemo(() => reports.data?.rows ?? [], [reports.data]);

	const allStretches = useMemo(() => toStretchesFromReports(allReports), [allReports]);

	const isRevision = !!jobSummary?.is_revision;

	const { rulerDuration, outOfRangeStretchIds, outOfRangeReportIds } = useOutOfRangeStretches({
		reports: allReports,
		videoDuration,
		isRevision
	});

	const draft = useStretchDraft({ reports: allReports, videoDuration, selectedReport });

	const persistence = useStretchPersistence({
		organizationId,
		companyId,
		beraJobSummaryId: jobSummaryId,
		reports: allReports,
		videoDuration
	});

	const loopRanges = useMemo(() => toRangesFromStretches(draft.listedStretches), [draft.listedStretches]);

	const { startLoop, handleProgress: keepLoop } = useStretchLoop({ playerRef, ranges: loopRanges, videoDuration });

	const loopStartedFor = useRef<string | undefined>(undefined);

	const ruler = useRulerEditing({
		rulerEdits: draft.rulerEdits,
		rulerStretches: draft.rulerStretches,
		selectedReport,
		clearRulerEdits: draft.clearRulerEdits,
		closeEditor: handleEditingClosed,
		saveRulerEdits: persistence.saveRulerEdits,
		onSaved: handleReportSaved
	});

	const canAddStretch = !!videoDuration && draft.nextFreeSlot !== null && !patch.isVideoUnavailable;

	useEffect(() => {
		if (!selectedReport) {
			loopStartedFor.current = undefined;

			return;
		}

		if (!loopRanges.length) {
			return;
		}

		if (loopStartedFor.current === selectedReport.id) {
			return;
		}

		loopStartedFor.current = selectedReport.id;

		startLoop();
	}, [selectedReport, loopRanges, startLoop]);

	const isLoading = tasks.isInitialLoading || reports.isInitialLoading;

	const isError = tasks.isError || reports.isError;

	function handleRefetch(): void {
		tasks.refetch();
		reports.refetch();
	}

	function handleReportSaved(): void {
		setSelectedReport(undefined);

		queryClient.invalidateQueries([QUERY_KEYS.GET_BERA_REPORTS_V2]);
	}

	function handleNoRiskSaved(): void {
		queryClient.invalidateQueries([QUERY_KEYS.GET_BERA_REPORTS_V2]);

		queryClient.invalidateQueries([QUERY_KEYS.GET_BERA_REPORT_V2]);
	}

	function handleDiscardReport(): void {
		setSelectedReport(undefined);
	}

	function handleEditingClosed(): void {
		draft.resetDraft();

		setSelectedReport(undefined);
	}

	function handleSelectReport(report: BeraReportV2): void {
		if (ruler.isEditMode) {
			return;
		}

		setSelectedReport(report);
	}

	const states: State = {
		file,
		jobSummary,
		organizationId,
		companyId,
		videoUrl: video.data,
		tasks: tasks.data ?? [],
		isFetchingTasks: tasks.isFetching,
		allReports,
		allStretches,
		playerRef,
		videoDuration,
		rulerDuration,
		isRevision,
		outOfRangeStretchIds,
		outOfRangeReportIds,
		selectedReport,
		selectedTaskId: draft.selectedTaskId,
		listedStretches: draft.listedStretches,
		rulerStretches: draft.rulerStretches,
		canAddStretch,
		hasUnsavedStretch: draft.hasUnsavedStretch,
		isDraggingStretch,
		hasUnsavedNoRiskRemoval,
		isEditMode: ruler.isEditMode,
		editableStretchIds: ruler.editableStretchIds,
		isSaving: persistence.isSaving,
		isVideoProcessing: patch.isVideoProcessing,
		isVideoCorrupted: patch.isVideoCorrupted,
		isVideoUnavailable: patch.isVideoUnavailable,
		videoProcessingError: patch.videoProcessingError,
		isLoading,
		isError,
		hoveredTaskId
	};

	const methods: Methods = {
		onVideoDurationChange: setVideoDuration,
		onRefetch: handleRefetch,
		onSelectReport: handleSelectReport,
		onDiscardReport: handleDiscardReport,
		onReportSaved: handleReportSaved,
		onKeepLoop: keepLoop,
		onAddStretch: draft.addStretch,
		onChangeStretch: draft.changeStretch,
		onRemoveStretch: draft.removeStretch,
		onResetDraft: draft.resetDraft,
		onSelectTask: draft.selectTask,
		onApplyRulerChange: draft.applyRulerChange,
		onStretchDragChange: setIsDraggingStretch,
		onUnsavedNoRiskRemovalChange: setHasUnsavedNoRiskRemoval,
		onNoRiskSaved: handleNoRiskSaved,
		onDropSelectedTaskStretches: draft.dropSelectedTaskStretches,
		onConfirmStretches: persistence.confirm,
		onRemoveStretches: persistence.remove,
		onToggleEditMode: ruler.toggleEditMode,
		onSelectStretches: ruler.selectStretches,
		onClearSelection: ruler.clearSelection,
		onPatchRetried: patch.onPatchRetried,
		onCancelEditMode: ruler.cancelEditMode,
		onHoverTask: setHoveredTaskId
	};

	const context: Context = {
		...states,
		...methods
	};

	return <BeraReportV2Context.Provider value={context}>{children}</BeraReportV2Context.Provider>;
}

export function useBeraReportV2(): Context {
	const context = useContext(BeraReportV2Context);

	if (!context) {
		throw new Error('useBeraReportV2 must be used within a BeraReportV2Provider');
	}

	return context;
}

export type { Context } from './types';
